import {
  CalendarDetailsVisibility,
  CalendarTaskSync,
  EntityLinks,
  ExternalConnection,
  LinkedItemGoal,
  Task,
  TaskCalendarOverride,
} from '@griply/types';
import { getCalendarSyncStates } from '@griply/utils';
import { useCallback, useMemo } from 'react';

export type UseCalendarTaskSyncOptions = {
  connections: ExternalConnection[];
  goals: LinkedItemGoal[];
  links?: EntityLinks;
  setTaskOverride: (task: Task, patch: Partial<TaskCalendarOverride>) => void;
};

export const useCalendarTaskSync = (
  tasks: Task[],
  { connections, goals, links, setTaskOverride }: UseCalendarTaskSyncOptions,
): CalendarTaskSync => {
  const states = useMemo(
    () => getCalendarSyncStates({ tasks, connections, goals, links }),
    [tasks, connections, goals, links],
  );

  const onToggleVisibility = useCallback(
    (task: Task, isHidden: boolean) =>
      setTaskOverride(task, {
        detailsVisibility: isHidden
          ? CalendarDetailsVisibility.Visible
          : CalendarDetailsVisibility.Hidden,
      }),
    [setTaskOverride],
  );

  const onToggleSync = useCallback(
    (task: Task, isSynced: boolean) =>
      setTaskOverride(task, { synced: !isSynced }),
    [setTaskOverride],
  );

  return useMemo(
    () => ({ states, onToggleVisibility, onToggleSync }),
    [states, onToggleVisibility, onToggleSync],
  );
};
